'use client';

import { useLocale } from 'next-intl';

import { useBookingsQuery } from '@website/app/[locale]/bookings/_hooks/useBookingsQuery';
import { useCurrentUser } from '@website/hooks/useCurrentUser';

interface UseUpcomingBookingsCountReturn {
  /** Number of upcoming confirmed bookings (same definition as /bookings' "Upcoming" tab). */
  count: number;
  isLoading: boolean;
}

/**
 * Count of upcoming confirmed bookings for the authenticated user — used as
 * a badge on the bookings nav icon (desktop header + mobile bottom nav).
 *
 * Reuses `useBookingsQuery` and, crucially, passes the same `{ locale }`
 * filter shape as `BookingsPageClient` when no activity filter is active.
 * The resulting react-query key is identical — `['bookings', userId,
 * isProfessional, undefined, locale]` — so the badge and the bookings list
 * share a single cache entry in the common case: no double network request,
 * and the badge never contradicts what the user sees on /bookings.
 *
 * Cache-sharing caveat: when the user applies an activity filter on the
 * bookings page, that page's cache key changes (the 4th slot becomes the
 * filtered activity-id list) and the badge stays on the unfiltered entry —
 * which is intentional. "Upcoming bookings" on the badge means *all* upcoming
 * bookings, not "upcoming bookings matching your current filter". The cost
 * is one extra list fetch for the badge, which we accept until a dedicated
 * count endpoint exists.
 *
 * "Upcoming" matches what `getUnifiedBookings` computes: confirmed bookings
 * whose end time hasn't passed yet. Same definition as /bookings' "Upcoming"
 * tab, so the badge and the list can never disagree.
 *
 * We don't add extra polling here because bookings don't change frequently
 * outside of user action and the 5-minute `staleTime` on `useBookingsQuery`
 * keeps traffic low. If near-real-time freshness becomes important,
 * refetchOnWindowFocus would be a natural place to turn on.
 *
 * Returns `{ count: 0 }` (not null) when the user is unauthenticated or the
 * query hasn't resolved — callers can simply render the badge when `count > 0`
 * and it'll be hidden during those states.
 */
export function useUpcomingBookingsCount(): UseUpcomingBookingsCountReturn {
  const locale = useLocale();
  const { data: user } = useCurrentUser();

  const { data, isLoading } = useBookingsQuery(user ?? null, { locale });

  return {
    count: data?.upcoming.length ?? 0,
    isLoading
  };
}
